import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiError, api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';

interface ContractState {
  available: boolean; version: number | null; required: boolean;
  contract: { number: string; version: number; status: 'issued' | 'uploaded' | 'accepted' | 'returned'; signedName: string | null; uploadedAt: string | null; reviewNote: string; reviewedAt: string | null } | null;
}

const STATUS: Record<string, string> = { issued: 'Atsisiųsta, laukiama pasirašytos', uploaded: 'Pasirašyta, tikrinama', accepted: 'Priimta', returned: 'Grąžinta pataisyti' };

/** Nustatymai → Sutartis: the agreement with Arkabi — download, sign with GoSign, upload. */
export function ContractCard({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['contract'], queryFn: () => api.get<ContractState>('api/v1/contract') });
  const [file, setFile] = useState<File | null>(null);
  const upload = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append('file', file!);
      const response = await fetch('api/v1/contract/signed', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
      const json = await response.json().catch(() => null);
      if (!response.ok) throw new ApiError(response.status, json?.error?.code ?? 'server_error');
    },
    onSuccess: async () => { setFile(null); await Promise.all(['contract', 'me'].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))); },
  });
  const d = data.data;
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const c = d?.contract;
  const submit = (e: FormEvent) => { e.preventDefault(); if (file) upload.mutate(); };
  return (
    <section className="panel-card account-data">
      <p className="eyebrow">{t('Sutartis')}</p><h2>{t('Paslaugų teikimo sutartis su Arkabi')}</h2>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {d && !d.available ? <p className="muted">{t('Sutartis dar ruošiama. Kai ji bus paruošta, ją rasite čia.')}</p> : null}
      {d?.available ? <>
        {!d.required ? <p className="muted">{t('Jūsų planui pasirašyti sutarties nereikia.')}</p> : null}
        {c ? <p><span className={`badge ${c.status === 'accepted' ? 'badge--paid' : c.status === 'returned' ? 'badge--overdue' : 'badge--sent'}`}>{t(STATUS[c.status] ?? c.status)}</span> <span className="muted">{c.number}</span></p> : null}
        {c?.status === 'returned' && c.reviewNote ? <Alert kind="error">{t('Pastaba')}: {c.reviewNote}</Alert> : null}
        <ol className="contract-steps">
          <li><a className="button button--ghost button--small" href="api/v1/contract/pdf">{t('Atsisiųsti sutartį (PDF)')}</a></li>
          <li>{t('Pasirašykite ją kvalifikuotu elektroniniu parašu per')} <a href="https://www.gosign.lt/lt/" target="_blank" rel="noreferrer">GoSign ↗</a> {t('(nemokama, Registrų centras; naudokite Mobilų parašą, Smart-ID su kvalifikuotu parašu ar asmens tapatybės kortelę).')}</li>
          <li>{t('Įkelkite pasirašytą PDF (arba ADOC) čia.')}</li>
          {d.required ? <li>{t('Tada skiltyje „Planas“ užsisakykite kortele.')}</li> : null}
        </ol>
        {c?.status === 'accepted' ? null : owner ? (
          <form className="contract-upload" onSubmit={submit}>
            <input type="file" accept=".pdf,.adoc,.asice,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <button className="button button--primary" disabled={!file || upload.isPending}>{upload.isPending ? t('Įkeliama…') : t('Įkelti pasirašytą sutartį')}</button>
          </form>
        ) : <p className="muted">{t('Pasirašytą sutartį įkelia įmonės savininkas.')}</p>}
        {upload.isError ? <Alert kind="error">{errorText(t, upload.error)}</Alert> : null}
        {c?.signedName ? <p className="muted">{t('Įkelta')}: <a href="api/v1/contract/signed">{c.signedName}</a>{c.uploadedAt ? ` · ${c.uploadedAt.slice(0, 10)}` : ''}</p> : null}
      </> : null}
    </section>
  );
}
